import React, { useEffect, useState } from 'react';
import { CheckCircle, Loader2, ArrowRight, Sparkles } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../contexts/AuthContext';
import WorkdayClientHeader from '../../components/workday-client/WorkdayClientHeader';
import { getBillingState, BillingSubscription } from '../../lib/billingState';

const MAX_ATTEMPTS  = 15;   // 15 × 2s = 30s total
const POLL_INTERVAL = 2000; // ms

export default function BillingSuccess() {
  const navigate      = useNavigate();
  const [params]      = useSearchParams();
  const { user }      = useAuth();

  const cycle = params.get('cycle') ?? 'monthly';

  const [activated, setActivated] = useState(false);
  const [timedOut,  setTimedOut]  = useState(false);
  const [attempts,  setAttempts]  = useState(0);

  // Poll billing_subscriptions — the authoritative source of truth.
  // PRO_ACTIVE = webhook confirmed. PENDING_ACTIVATION = payment received but webhook
  // in flight — treat as success immediately since payment is confirmed by PayPal.
  useEffect(() => {
    if (!user?.id) return;

    let stopped = false;

    const check = async (): Promise<boolean> => {
      const { data } = await supabase
        .from('billing_subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      const state = getBillingState(data as BillingSubscription | null);
      // Accept either confirmed or in-flight activation — payment is already captured
      if (state === 'PRO_ACTIVE' || state === 'PENDING_ACTIVATION') {
        if (!stopped) setActivated(true);
        return true;
      }
      return false;
    };

    const run = async () => {
      // Immediate check first — webhook may have already arrived
      if (await check()) return;

      let count = 1;

      const interval = setInterval(async () => {
        if (stopped) { clearInterval(interval); return; }

        count++;
        setAttempts(count);

        const done = await check();
        if (done || count >= MAX_ATTEMPTS) {
          clearInterval(interval);
          if (!done && !stopped) setTimedOut(true);
        }
      }, POLL_INTERVAL);
    };

    run();

    return () => { stopped = true; };
  }, [user?.id]);

  // Auto-navigate to dashboard once activated
  useEffect(() => {
    if (!activated) return;
    const t = setTimeout(() => navigate('/workday-client/dashboard'), 2000);
    return () => clearTimeout(t);
  }, [activated, navigate]);

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      <WorkdayClientHeader />

      <div className="max-w-lg mx-auto px-4 pt-36 pb-24 flex flex-col items-center text-center">

        {activated ? (
          /* ── Success ──────────────────────────────────────────────────── */
          <>
            <div className="relative mb-8">
              <div className="w-24 h-24 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
                <CheckCircle className="w-12 h-12 text-emerald-400" />
              </div>
              <div className="absolute -top-1 -right-1">
                <Sparkles className="w-6 h-6 text-amber-400" />
              </div>
            </div>

            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-semibold uppercase tracking-widest mb-5">
              <CheckCircle className="w-3.5 h-3.5" /> Pro Activated
            </div>

            <h1 className="text-3xl font-extrabold text-white mb-3">
              Welcome to PerplexityATS Pro!
            </h1>
            <p className="text-gray-400 text-sm mb-8 leading-relaxed">
              Your {cycle === 'yearly' ? 'yearly' : 'monthly'} Pro subscription payment was confirmed. Your account is activating — all Pro features will be available within a minute. Redirecting you to your dashboard…
            </p>

            <div className="grid grid-cols-1 gap-3 w-full mb-8">
              {[
                { label: 'Post your first job', path: '/workday-client/jobs',        cta: 'Go to Job Board' },
                { label: 'Browse talent pool',  path: '/workday-client/talent',      cta: 'View Talent' },
                { label: 'Invite your team',    path: '/workday-client/invite-team', cta: 'Invite Team' },
              ].map(({ label, path, cta }) => (
                <button
                  key={path}
                  onClick={() => navigate(path)}
                  className="flex items-center justify-between w-full px-5 py-3.5 bg-white/[0.04] border border-white/10 rounded-xl hover:border-cyan-500/30 hover:bg-white/[0.07] transition-all group"
                >
                  <span className="text-gray-300 text-sm font-medium">{label}</span>
                  <div className="flex items-center gap-1.5 text-cyan-400 text-sm font-semibold">
                    {cta}
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                  </div>
                </button>
              ))}
            </div>

            <button
              onClick={() => navigate('/workday-client/billing')}
              className="text-gray-500 text-sm hover:text-gray-300 transition-colors"
            >
              View billing details
            </button>
          </>

        ) : timedOut ? (
          /* ── Timed out — payment received but webhook hasn't arrived yet ── */
          <>
            <div className="w-24 h-24 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mb-8">
              <CheckCircle className="w-12 h-12 text-amber-400" />
            </div>

            <h1 className="text-3xl font-extrabold text-white mb-3">
              Payment received!
            </h1>
            <p className="text-gray-400 text-sm mb-6 leading-relaxed">
              PayPal confirmed your payment. Your Pro plan is activating — it may take up to 1–2 minutes for the webhook to fully process. Check your billing page shortly.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 w-full">
              <button
                onClick={() => navigate('/workday-client/billing')}
                className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-cyan-500/15 border border-cyan-500/30 text-cyan-300 text-sm font-semibold hover:bg-cyan-500/25 transition-all"
              >
                Check billing status
                <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => navigate('/workday-client/dashboard')}
                className="flex-1 py-3 rounded-xl bg-white/[0.04] border border-white/10 text-gray-300 text-sm font-semibold hover:bg-white/[0.07] transition-all"
              >
                Go to dashboard
              </button>
            </div>
          </>

        ) : (
          /* ── Polling / waiting ────────────────────────────────────────── */
          <>
            <div className="relative mb-8">
              <div className="w-24 h-24 rounded-full bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center">
                <Loader2 className="w-10 h-10 text-cyan-400 animate-spin" />
              </div>
              <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-amber-400 animate-pulse" />
            </div>

            <h1 className="text-3xl font-extrabold text-white mb-3">
              Activating your Pro account…
            </h1>
            <p className="text-gray-400 text-sm mb-6 leading-relaxed">
              PayPal has confirmed your payment. Waiting for subscription activation — this usually takes 5–15 seconds.
            </p>

            <div className="flex gap-1.5 justify-center mb-6">
              {Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={i}
                  className="w-2 h-2 rounded-full bg-cyan-500 animate-bounce"
                  style={{ animationDelay: `${i * 0.15}s` }}
                />
              ))}
            </div>

            {attempts >= 6 && (
              <p className="text-gray-500 text-xs">
                Taking longer than expected — webhook delivery can occasionally take up to a minute.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
